import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Element } from '../../../../../../../common/html/element'
import { Notification } from '../../../../../../../common/notification'
import { FORMAT, formatDateTime } from '../../../../../../../helpers/datetime'
import { valToPercent } from '../../../../../../../helpers/value'
import { selectRiskScaleInterpretation } from '../../../../store/slice'
import classes from './index.module.scss'

export const TableRiskScaleAndInterpretation = ({ isDynamicReport }) => {
  const locale = useSelector((state) => state.i18n.locale)
  const riskScaleInterpretation = useSelector(selectRiskScaleInterpretation)

  const getLastTime = useMemo(() => {
    if (riskScaleInterpretation && riskScaleInterpretation.length) {
      const time = riskScaleInterpretation[0]?.versionDateId
      return formatDateTime(time, FORMAT.DATE, locale)
    }
    return '-'
  }, [riskScaleInterpretation, locale])

  const headers = riskScaleInterpretation.map((item) => ({
    label: item.riskGradeTypeCode,
    color: item.scoreRangeCode,
  }))

  const rows = [
    {
      label: 'riskScaleAndInterpretation.riskScore',
      values: riskScaleInterpretation.map((item) => ({
        value:
          item.scoreRange_Min && item.scoreRange_Max
            ? `${item.scoreRange_Min}-${item.scoreRange_Max}`
            : '0',
      })),
    },
    {
      label: 'riskScaleAndInterpretation.riskCategory',
      values: riskScaleInterpretation.map((item) => ({
        value: item.scoreRangeName,
      })),
    },
    {
      label: 'riskScaleAndInterpretation.probabilityOfDefault',
      values: riskScaleInterpretation.map((item) => ({
        value: valToPercent(item.pd, locale, false, true),
      })),
    },
  ]

  const groupCells = (values) => {
    const result = []
    let i = 0
    while (i < values.length) {
      let count = 1
      while (
        i + count < values.length &&
        values[i].value === values[i + count].value
      ) {
        count++
      }
      result.push({ ...values[i], colSpan: count })
      for (let j = 1; j < count; j++) {
        result.push({ ...values[i + j], colSpan: 0 })
      }
      i += count
    }
    return result
  }

  if (!riskScaleInterpretation.length) return <Notification />

  if (isDynamicReport) {
    return (
      <div
        className={classes.table}
        style={{
          margin: '0px',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
            fontSize: 8,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 2px',
                  width: 50,
                }}
              >
                <Translate value="riskScaleAndInterpretation.riskGrade" />
              </th>
              {headers.map((h, idx) => (
                <th
                  key={idx}
                  style={{
                    background: h.color || '',
                    padding: '8px 2px',
                  }}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => {
              let values = row.values
              if (row.label === 'riskScaleAndInterpretation.riskCategory') {
                values = groupCells(row.values)
              }
              return (
                <tr key={rowIdx}>
                  <td
                    style={{
                      padding: '8px 2px',
                      fontWeight: 600,
                      textAlign: 'left',
                    }}
                  >
                    <Translate value={row.label} />
                  </td>
                  {values.map((cell, cellIdx) =>
                    cell.colSpan !== 0 ? (
                      <td
                        key={cellIdx}
                        colSpan={cell.colSpan || 1}
                        style={{
                          padding: '8px 2px',
                          textAlign: 'center',
                        }}
                      >
                        {cell.value}
                      </td>
                    ) : null,
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
        <div className={classes.descriptionTable}>
          <Translate value="riskScaleAndInterpretation.footer" /> {getLastTime}
        </div>
      </div>
    )
  }

  return (
    <div
      className={classes.table}
      style={{
        margin: '0 6px 6px',
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            <Element
              elementName="th"
              style={{
                textAlign: 'left',
                padding: '8px',
                width: '150px',
              }}
            >
              <Translate value="riskScaleAndInterpretation.riskGrade" />
            </Element>
            {headers.map((h, idx) => (
              <Element
                key={idx}
                elementName="th"
                style={{
                  background: h.color || '',
                  padding: '8px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {h.label}
              </Element>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => {
            let values = row.values
            if (row.label === 'riskScaleAndInterpretation.riskCategory') {
              values = groupCells(row.values)
            }
            return (
              <tr key={rowIdx}>
                <Element
                  elementName="td"
                  style={{
                    padding: '8px',
                    textWrap: 'nowrap',
                    fontWeight: 600,
                    textAlign: 'left',
                  }}
                >
                  <Translate value={row.label} />
                </Element>
                {values.map((cell, cellIdx) =>
                  cell.colSpan !== 0 ? (
                    <Element
                      key={cellIdx}
                      elementName="td"
                      colSpan={cell.colSpan || 1}
                      style={{
                        padding: '8px 2px',
                        textAlign: 'center',
                      }}
                    >
                      {cell.value}
                    </Element>
                  ) : null,
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className={classes.descriptionTable}>
        <Translate value="riskScaleAndInterpretation.footer" /> {getLastTime}
      </div>
    </div>
  )
}
